import React, { useState } from 'react';

// Netlify's Prerender (and similar crawler renderers) often block image requests, which fires onError even
// though the file is fine. Swapping to initials then would hand crawlers a snapshot with no <img> at all, so
// image errors are only acted on in a real browser. Same test index.html uses for the service worker.
const isAutomatedBrowser = (): boolean =>
  typeof navigator !== 'undefined' &&
  (!!(navigator as any).webdriver || /HeadlessChrome|Prerender|bot|crawl|spider|Lighthouse/i.test(navigator.userAgent));

interface PersonAvatarProps {
  name: string;
  imageUrl?: string;
  /** Applied to the photo and to the initials fallback alike, so both occupy exactly the same box. */
  className?: string;
  /** Text size for the initials fallback, e.g. "text-3xl". */
  initialsClassName?: string;
  width?: number;
  height?: number;
}

const initialsOf = (name: string): string =>
  name
    .replace(/,.*$/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

/**
 * A person's photo, or their initials when there is no photo or it fails to load. The attorney photo on
 * Cloudinary was deleted at some point, which left a broken-image icon on every article by that author (and
 * a broken <img> in every crawl); an initials badge keeps the layout intact until a real photo is supplied.
 */
const PersonAvatar: React.FC<PersonAvatarProps> = ({ name, imageUrl, className = '', initialsClassName = 'text-2xl', width, height }) => {
  const [failed, setFailed] = useState(false);

  if (!imageUrl || failed) {
    return (
      <div
        role="img"
        aria-label={name}
        className={`${className} flex items-center justify-center bg-brand-gold/20 border border-brand-gold/40 text-brand-gold-light font-serif font-bold ${initialsClassName}`}
      >
        {initialsOf(name)}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={name}
      className={`${className} object-cover object-top`}
      width={width}
      height={height}
      loading="lazy"
      onError={() => { if (!isAutomatedBrowser()) setFailed(true); }}
    />
  );
};

export default PersonAvatar;
